import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AntecedentesTab } from './AntecedentesTab';
import { MedicamentosActivosTab } from './MedicamentosActivosTab';
import { NotasPacienteTab } from './NotasPacienteTab';
import { DocumentosPacienteTab } from './DocumentosPacienteTab';
import { SignosVitalesTab } from './SignosVitalesTab';
import { ExploracionesTab } from './exploraciones/ExploracionesTab';
import { ExploracionesDinamicasTab } from './ExploracionesDinamicasTab';
import { RecetasTab } from './RecetasTab';
import { EstudiosTab } from './EstudiosTab';
import { ConsentimientosTab } from './ConsentimientosTab';
import { DiagnosticosTab } from './DiagnosticosTab';
import { FinanzasTab } from './FinanzasTab';
import { ArcoTab } from './ArcoTab';
import { TareasRelacionadasPanel } from '@/components/tareas/TareasRelacionadasPanel';
import { ROLES_TAREAS } from '@/hooks/useTareas';
import {
  ArrowLeft, User, Phone, Mail, Droplet, AlertTriangle, Building2,
  Stethoscope, Calendar, Pencil, X, Plus, Activity, Pill,
  FlaskConical, ClipboardList, FileCheck, HeartPulse, Clock,
  ChevronDown, FileText, StickyNote, FolderOpen, TrendingUp, Wallet, Shield, FileEdit, ListTodo,
} from 'lucide-react';
import type { ElementType } from 'react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { formatDate, getInitials, cn } from '@/lib/utils';
import { useAuthStore, ROL } from '@/store/auth.store';
import { getErrorMessage } from '@/lib/errors';
import { useConfiguracionClinica, moduloAplica } from '@/hooks/useConfiguracionClinica';
import { getEstadoCitaInfo } from '@/types/cita';

const GENERO_LABEL: Record<string, string> = {
  masculino: 'Masculino', femenino: 'Femenino', otro: 'Otro', no_especificado: 'No especificado',
};
const SANGRE_COLOR: Record<string, string> = {
  'A+': 'bg-red-50 text-red-700', 'A-': 'bg-red-100 text-red-800',
  'B+': 'bg-orange-50 text-orange-700', 'B-': 'bg-orange-100 text-orange-800',
  'AB+': 'bg-purple-50 text-purple-700', 'AB-': 'bg-purple-100 text-purple-800',
  'O+': 'bg-blue-50 text-blue-700', 'O-': 'bg-blue-100 text-blue-800',
};
const GENEROS = ['masculino', 'femenino', 'otro', 'no_especificado'];
const TIPOS_SANGRE = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'desconocido'];
const ESTADO_CONSULTA: Record<string, { label: string; cls: string }> = {
  abierta:    { label: 'En consulta', cls: 'bg-blue-50 text-blue-700' },
  finalizada: { label: 'Finalizada',  cls: 'bg-green-50 text-green-700' },
  cancelada:  { label: 'Cancelada',   cls: 'bg-red-50 text-red-700' },
};

const ROLES_EXPEDIENTE = [ROL.SUPER_ADMIN, ROL.MEDICO, ROL.MEDICO_ADMIN];
const ROLES_EDITAR     = [ROL.SUPER_ADMIN, ROL.MEDICO, ROL.MEDICO_ADMIN, ROL.ASISTENTE];
const ROLES_CITAS      = [ROL.SUPER_ADMIN, ROL.MEDICO, ROL.MEDICO_ADMIN, ROL.RECEPCIONISTA, ROL.ASISTENTE];

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300';

function calcularEdad(fn: string | Date): number {
  const hoy = new Date();
  const nac = new Date(fn);
  let edad = hoy.getFullYear() - nac.getFullYear();
  const m = hoy.getMonth() - nac.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
  return edad;
}

function duracionMin(inicio: string, fin: string): number {
  return Math.round((new Date(fin).getTime() - new Date(inicio).getTime()) / 60000);
}

type NavLeaf  = { type: 'single'; id: string; label: string; icon: ElementType; requiresExpediente?: boolean; requiereModulo?: string; requiereRol?: string[] };
type NavGroup = { type: 'group';  id: string; label: string; icon: ElementType; items: NavLeaf[] };
type NavItem  = NavLeaf | NavGroup;

const NAV_ITEMS: NavItem[] = [
  { type: 'single', id: 'consultas', label: 'Consultas', icon: Stethoscope },
  {
    type: 'group', id: 'clinico', label: 'Clínico', icon: Activity,
    items: [
      { type: 'single', id: 'signos-vitales', label: 'Signos vitales',      icon: TrendingUp,  requiresExpediente: true, requiereModulo: 'signos_vitales_nucleo' },
      { type: 'single', id: 'diagnosticos',   label: 'Diagnósticos',        icon: Activity,    requiresExpediente: true, requiereModulo: 'diagnosticos_lectura' },
      { type: 'single', id: 'exploraciones',  label: 'Exploraciones',       icon: HeartPulse,  requiresExpediente: true },
      { type: 'single', id: 'exploraciones-dinamicas', label: 'Formularios clínicos', icon: FileEdit, requiresExpediente: true, requiereModulo: 'formularios_dinamicos' },
      { type: 'single', id: 'recetas',        label: 'Recetas',             icon: Pill,        requiresExpediente: true, requiereModulo: 'recetas_emitir' },
      { type: 'single', id: 'medicamentos',   label: 'Medicamentos activos',icon: Pill,        requiresExpediente: true },
    ],
  },
  { type: 'single', id: 'estudios', label: 'Estudios', icon: FlaskConical, requiresExpediente: true, requiereModulo: 'estudios_lectura' },
  {
    type: 'group', id: 'documentos', label: 'Documentos', icon: FileText,
    items: [
      { type: 'single', id: 'antecedentes',    label: 'Antecedentes',    icon: ClipboardList, requiresExpediente: true },
      { type: 'single', id: 'consentimientos', label: 'Consentimientos', icon: FileCheck,     requiresExpediente: true },
      { type: 'single', id: 'notas',           label: 'Notas',           icon: StickyNote,    requiresExpediente: true },
      { type: 'single', id: 'archivos',        label: 'Archivos',        icon: FolderOpen,    requiresExpediente: false },
    ],
  },
  // finanzas_paciente no es una especialidad clínica — se gatea por rol de staff, igual que
  // el backend (VIEWERS en presupuestos.controller.ts), no por perfil de clínica ni de médico.
  { type: 'single', id: 'finanzas', label: 'Finanzas', icon: Wallet, requiereRol: [ROL.SUPER_ADMIN, ROL.ADMIN_CLINICA, ROL.MEDICO_ADMIN, ROL.MEDICO, ROL.RECEPCIONISTA] },
  // Tareas ligadas al paciente (incluye las de sus citas, consultas y presupuestos). Recepcionista no usa el módulo.
  { type: 'single', id: 'tareas',  label: 'Tareas', icon: ListTodo, requiereRol: ROLES_TAREAS },
  { type: 'single', id: 'arco',    label: 'ARCO / Privacidad', icon: Shield },
];

export function PacienteFichaPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { usuario } = useAuthStore();
  const { perfilTipo, modulosHabilitados } = useConfiguracionClinica();

  const canVerExpediente = !!(usuario?.rolId && (ROLES_EXPEDIENTE as string[]).includes(usuario.rolId));
  const canEditar        = !!(usuario?.rolId && (ROLES_EDITAR     as string[]).includes(usuario.rolId));
  const canAgendarCita   = !!(usuario?.rolId && (ROLES_CITAS      as string[]).includes(usuario.rolId));

  const [activeTab,       setActiveTab]       = useState('consultas');
  const [openGroup,       setOpenGroup]       = useState<string | null>(null);
  const [groupMenuPos,    setGroupMenuPos]    = useState<{ top: number; left: number } | null>(null);
  const [consultasSubTab, setConsultasSubTab] = useState<'activa' | 'proximas' | 'historial'>('proximas');
  const [editOpen,   setEditOpen]   = useState(false);
  const [nuevaCitaOpen, setNuevaCitaOpen] = useState(false);
  const [form, setForm] = useState({
    nombre: '', apellidoPaterno: '', apellidoMaterno: '',
    fechaNacimiento: '', genero: 'no_especificado', tipoSangre: 'desconocido',
    telefono: '', email: '', curp: '', alergias: '', medicoTitularId: '',
  });
  const [citaForm, setCitaForm] = useState({
    medicoId: '', fecha: '', hora: '', duracion: 30,
    tipo: 'presencial' as 'presencial' | 'teleconsulta', motivo: '',
  });

  const { data: paciente, isLoading } = useQuery({
    queryKey: ['paciente', id],
    queryFn: () => api.get(`/api/pacientes/${id}`).then(r => r.data),
  });

  const { data: exp } = useQuery({
    queryKey: ['expediente', id],
    queryFn: () => api.get(`/api/pacientes/${id}/expediente`).then(r => r.data),
    enabled: canVerExpediente,
  });

  const { data: consultas = [] } = useQuery({
    queryKey: ['consultas', 'paciente', id],
    queryFn: () => api.get('/api/consultas', { params: { pacienteId: id } }).then(r => r.data),
  });

  const { data: citas = [] } = useQuery({
    queryKey: ['citas', 'paciente', id],
    queryFn: () => api.get('/api/citas', { params: { pacienteId: id } }).then(r => r.data),
  });

  const { data: medicos = [] } = useQuery({
    queryKey: ['medicos-select'],
    queryFn: () => api.get('/api/medicos').then(r => r.data),
    enabled: editOpen || nuevaCitaOpen,
  });

  const clinicaIdParaCita = paciente?.clinicaId || usuario?.clinicaId;
  const { data: slotsData, isLoading: loadingSlots } = useQuery({
    queryKey: ['disponibilidad', citaForm.medicoId, citaForm.fecha, citaForm.duracion, clinicaIdParaCita],
    queryFn: () => api.get('/api/citas/disponibilidad', {
      params: { medicoId: citaForm.medicoId, clinicaId: clinicaIdParaCita, fecha: citaForm.fecha, duracion: citaForm.duracion },
    }).then(r => r.data as { slots: { inicio: string; fin: string }[]; mensaje?: string }),
    enabled: !!(nuevaCitaOpen && citaForm.medicoId && citaForm.fecha && clinicaIdParaCita),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (paciente && editOpen) {
      setForm({
        nombre:           paciente.nombre           || '',
        apellidoPaterno:  paciente.apellidoPaterno  || '',
        apellidoMaterno:  paciente.apellidoMaterno  || '',
        fechaNacimiento:  paciente.fechaNacimiento  ? paciente.fechaNacimiento.split('T')[0] : '',
        genero:           paciente.genero           || 'no_especificado',
        tipoSangre:       paciente.tipoSangre       || 'desconocido',
        telefono:         paciente.telefono         || '',
        email:            paciente.email            || '',
        curp:             paciente.curp             || '',
        alergias:         paciente.alergias         || '',
        medicoTitularId:  paciente.medicoTitularId  || '',
      });
    }
  }, [paciente, editOpen]);

  const updateMutation = useMutation({
    mutationFn: (dto: any) => api.patch(`/api/pacientes/${id}`, dto).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['paciente', id] });
      toast.success('Paciente actualizado');
      setEditOpen(false);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar el paciente')),
  });

  const citaMutation = useMutation({
    mutationFn: (dto: any) => api.post('/api/citas', dto).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['citas', 'paciente', id] });
      toast.success('Cita agendada');
      setNuevaCitaOpen(false);
      setCitaForm({ medicoId: '', fecha: '', hora: '', duracion: 30, tipo: 'presencial', motivo: '' });
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al agendar la cita')),
  });

  const ff = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const cf = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setCitaForm(prev => {
      const next = { ...prev, [field]: e.target.value as any };
      if (['medicoId', 'fecha', 'duracion'].includes(field)) next.hora = '';
      return next;
    });

  function handleSubmitEdit(e: React.FormEvent) {
    e.preventDefault();
    const dto: any = { ...form };
    if (!dto.apellidoMaterno) delete dto.apellidoMaterno;
    if (!dto.curp) delete dto.curp;
    if (!dto.alergias) delete dto.alergias;
    updateMutation.mutate(dto);
  }

  function handleSubmitCita(e: React.FormEvent) {
    e.preventDefault();
    if (!citaForm.medicoId || !citaForm.fecha || !citaForm.hora) {
      toast.error('Selecciona médico, fecha y un horario disponible');
      return;
    }
    const fechaInicio = new Date(`${citaForm.fecha}T${citaForm.hora}:00`);
    const fechaFin    = new Date(fechaInicio.getTime() + citaForm.duracion * 60000);
    citaMutation.mutate({
      pacienteId: id,
      medicoId:   citaForm.medicoId,
      clinicaId:  paciente?.clinicaId || usuario?.clinicaId,
      fechaInicio,
      fechaFin,
      tipo:   citaForm.tipo,
      motivo: citaForm.motivo || undefined,
    });
  }

  if (isLoading) return <div className="text-center py-16 text-text-secondary text-sm">Cargando...</div>;
  if (!paciente) return (
    <div className="text-center py-16">
      <p className="font-medium text-text-primary">Paciente no encontrado</p>
      <button onClick={() => navigate('/pacientes')} className="mt-3 text-sm text-primary-600 hover:underline">Volver</button>
    </div>
  );

  const nombre  = `${paciente.nombre} ${paciente.apellidoPaterno}${paciente.apellidoMaterno ? ' ' + paciente.apellidoMaterno : ''}`;
  const edad    = paciente.fechaNacimiento ? calcularEdad(paciente.fechaNacimiento) : null;
  const exp_    = exp;
  const hist    = exp_?.historiaClinica;
  const ahora   = new Date();

  // Next upcoming cita for the header
  const citaProxima = (citas as any[])
    .filter(c => new Date(c.fechaInicio) > ahora && c.estado !== 'cancelada')
    .sort((a, b) => new Date(a.fechaInicio).getTime() - new Date(b.fechaInicio).getTime())[0] ?? null;

  // Citas split for the Consultas tab
  const citaActiva    = (citas as any[]).find(c => c.estado === 'en_curso') ?? null;
  const citasFuturas  = (citas as any[])
    .filter(c => c.estado !== 'en_curso' && new Date(c.fechaInicio) > ahora)
    .sort((a, b) => new Date(a.fechaInicio).getTime() - new Date(b.fechaInicio).getTime());
  const citasPasadas  = (citas as any[])
    .filter(c => c.estado !== 'en_curso' && new Date(c.fechaInicio) <= ahora)
    .sort((a, b) => new Date(b.fechaInicio).getTime() - new Date(a.fechaInicio).getTime());

  function isLeafVisible(leaf: NavLeaf) {
    if (leaf.requiresExpediente && !canVerExpediente) return false;
    if (leaf.requiereModulo && !moduloAplica(leaf.requiereModulo, perfilTipo, modulosHabilitados)) return false;
    if (leaf.requiereRol && !(usuario?.rolId && leaf.requiereRol.includes(usuario.rolId))) return false;
    return true;
  }

  const visibleNav: NavItem[] = NAV_ITEMS.flatMap((item): NavItem[] => {
    if (item.type === 'single') return isLeafVisible(item) ? [item] : [];
    const visibleItems = item.items.filter(isLeafVisible);
    return visibleItems.length ? [{ ...item, items: visibleItems } as NavGroup] : [];
  });

  function getActiveLeaf(): NavLeaf | undefined {
    for (const item of NAV_ITEMS) {
      if (item.type === 'single' && item.id === activeTab) return item;
      if (item.type === 'group') {
        const found = item.items.find(i => i.id === activeTab);
        if (found) return found;
      }
    }
  }

  function selectTab(tabId: string) {
    setActiveTab(tabId);
    setOpenGroup(null);
  }

  function getTabCount(tabId: string): number | null {
    switch (tabId) {
      case 'consultas':      return (citas as any[]).length + (consultas as any[]).length || null;
      case 'diagnosticos':   return exp_?.diagnosticos?.length  ?? null;
      case 'exploraciones':  return exp_?.exploraciones?.length ?? null;
      case 'recetas':        return exp_?.recetas?.length       ?? null;
      case 'estudios':       return ((exp_?.analisis?.length ?? 0) + (exp_?.laboratorios?.length ?? 0)) || null;
      case 'consentimientos':return exp_?.consentimientos?.length ?? null;
      default:               return null;
    }
  }

  const activeLeafItem  = getActiveLeaf();
  const isActiveInGroup = NAV_ITEMS.some(
    item => item.type === 'group' && (item as NavGroup).items.some(i => i.id === activeTab)
  );
  const ActiveLeafIcon = activeLeafItem?.icon;

  return (
    <div className={cn('flex flex-col gap-4', activeTab !== 'antecedentes' && 'h-full')}>
      {/* Breadcrumb */}
      <button onClick={() => navigate('/pacientes')}
        className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors self-start">
        <ArrowLeft size={14} /> Pacientes
      </button>

      {/* ── Row 1: Header info card ── */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden flex-shrink-0">
        <div className="flex flex-col md:flex-row md:divide-x divide-y md:divide-y-0 divide-gray-100">

          {/* Generales */}
          <div className="flex-1 p-4 md:p-5 flex items-start gap-3 md:gap-4">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-xl font-bold flex-shrink-0">
              {getInitials(nombre)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h2 className="font-semibold text-text-primary leading-snug">{nombre}</h2>
                {canEditar && (
                  <button onClick={() => setEditOpen(true)}
                    className="flex items-center gap-1 border border-gray-200 text-text-secondary px-2.5 py-1 rounded-lg text-xs hover:bg-gray-50 hover:text-text-primary transition-colors flex-shrink-0">
                    <Pencil size={11} /> Editar
                  </button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                {paciente.fechaNacimiento && (
                  <span className="flex items-center gap-1 text-xs text-text-secondary">
                    <Calendar size={11} />
                    {formatDate(paciente.fechaNacimiento)}
                    {edad !== null && <span className="text-text-primary font-medium">· {edad} años</span>}
                  </span>
                )}
                {paciente.genero && paciente.genero !== 'no_especificado' && (
                  <span className="flex items-center gap-1 text-xs text-text-secondary">
                    <User size={11} />
                    {GENERO_LABEL[paciente.genero]}
                  </span>
                )}
                {paciente.tipoSangre && paciente.tipoSangre !== 'desconocido' && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${SANGRE_COLOR[paciente.tipoSangre] || 'bg-gray-100 text-gray-600'}`}>
                    <Droplet size={9} />{paciente.tipoSangre}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                {paciente.telefono && (
                  <span className="flex items-center gap-1 text-xs text-text-secondary"><Phone size={11} />{paciente.telefono}</span>
                )}
                {paciente.email && (
                  <span className="flex items-center gap-1 text-xs text-text-secondary"><Mail size={11} />{paciente.email}</span>
                )}
                {paciente.medicoTitular && (
                  <span className="flex items-center gap-1 text-xs text-text-secondary"><Stethoscope size={11} />{paciente.medicoTitular.nombre}</span>
                )}
              </div>
            </div>
          </div>

          {/* Cita */}
          <div className="md:w-72 md:flex-shrink-0 p-4 md:p-5 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">
                {citaProxima ? 'Próxima cita' : 'Sin citas próximas'}
              </p>
              {canAgendarCita && (
                <button onClick={() => setNuevaCitaOpen(true)}
                  className="flex items-center gap-1 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium px-2.5 py-1 rounded-lg transition-colors">
                  <Plus size={11} /> Nueva
                </button>
              )}
            </div>

            {citaProxima ? (
              <div className="space-y-2">
                {citaProxima.motivo && (
                  <div>
                    <p className="text-[10px] text-text-secondary uppercase tracking-wide">Motivo</p>
                    <p className="text-xs font-medium text-text-primary mt-0.5">{citaProxima.motivo}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <div>
                    <p className="text-[10px] text-text-secondary uppercase tracking-wide">Fecha</p>
                    <p className="text-xs font-medium text-text-primary mt-0.5">
                      {new Date(citaProxima.fechaInicio).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-text-secondary uppercase tracking-wide">Hora</p>
                    <p className="text-xs font-medium text-text-primary mt-0.5">
                      {new Date(citaProxima.fechaInicio).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  {citaProxima.fechaFin && (
                    <div>
                      <p className="text-[10px] text-text-secondary uppercase tracking-wide">Duración</p>
                      <p className="text-xs font-medium text-text-primary mt-0.5">
                        {duracionMin(citaProxima.fechaInicio, citaProxima.fechaFin)} min
                      </p>
                    </div>
                  )}
                  {citaProxima.medico && (
                    <div>
                      <p className="text-[10px] text-text-secondary uppercase tracking-wide">Médico</p>
                      <p className="text-xs font-medium text-text-primary mt-0.5 truncate">{citaProxima.medico.nombre}</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-xs text-text-secondary">No hay citas agendadas</p>
            )}
          </div>
        </div>

        {/* Alergias warning strip */}
        {paciente.alergias && (
          <div className="border-t border-amber-200 bg-amber-50 px-5 py-2.5 flex items-center gap-2">
            <AlertTriangle size={13} className="text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-800">
              <span className="font-semibold">Alergias: </span>{paciente.alergias}
            </p>
          </div>
        )}
      </div>

      {/* ── Row 2: Tabs + content ── */}
      <div className={cn(
        'bg-white rounded-xl border border-gray-100 flex flex-col',
        activeTab !== 'antecedentes' && 'flex-1 min-h-0 overflow-hidden',
      )}>

        {/* Mobile tab select */}
        <div className="md:hidden px-3 py-2.5 border-b border-gray-100 flex-shrink-0 relative">
          <select
            value={activeTab}
            onChange={e => { setActiveTab(e.target.value); setOpenGroup(null); }}
            className="w-full appearance-none px-4 py-2.5 pr-10 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300"
          >
            {visibleNav.flatMap(item =>
              item.type === 'single'
                ? [<option key={item.id} value={item.id}>{item.label}</option>]
                : (item as NavGroup).items.map(sub => (
                    <option key={sub.id} value={sub.id}>{sub.label}</option>
                  ))
            )}
          </select>
          <ChevronDown size={16} className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>

        {/* Nav bar — desktop */}
        <div
          className="hidden md:flex items-stretch border-b border-gray-100 flex-shrink-0 px-1 relative overflow-x-auto"
          onScroll={() => openGroup && setOpenGroup(null)}
        >
          {visibleNav.map(item => {
            if (item.type === 'single') {
              const count    = getTabCount(item.id);
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => selectTab(item.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-4 py-3 text-xs font-medium whitespace-nowrap border-b-2 -mb-px transition-colors',
                    isActive
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-text-secondary hover:text-text-primary'
                  )}
                >
                  <item.icon size={13} />
                  {item.label}
                  {count !== null && count > 0 && (
                    <span className={cn(
                      'px-1.5 py-0.5 rounded-full text-[10px] font-semibold',
                      isActive ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-text-secondary'
                    )}>{count}</span>
                  )}
                </button>
              );
            }

            const groupItem = item as NavGroup;
            const isGroupActive = groupItem.items.some(i => i.id === activeTab);
            const isOpen        = openGroup === groupItem.id;

            return (
              <div key={groupItem.id} className="relative flex items-stretch">
                <button
                  onClick={(e) => {
                    if (isOpen) {
                      setOpenGroup(null);
                    } else {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setGroupMenuPos({ top: rect.bottom + 4, left: rect.left });
                      setOpenGroup(groupItem.id);
                    }
                  }}
                  className={cn(
                    'flex items-center gap-1.5 px-4 py-3 text-xs font-medium whitespace-nowrap border-b-2 -mb-px transition-colors',
                    isGroupActive
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-text-secondary hover:text-text-primary'
                  )}
                >
                  <groupItem.icon size={13} />
                  {groupItem.label}
                  <ChevronDown size={11} className={cn('ml-0.5 transition-transform duration-150', isOpen && 'rotate-180')} />
                </button>

                {isOpen && groupMenuPos && (
                  <div
                    className="fixed bg-white border border-gray-100 rounded-xl shadow-lg z-20 min-w-[168px] py-1 overflow-hidden"
                    style={{ top: groupMenuPos.top, left: groupMenuPos.left }}
                  >
                    {groupItem.items.map(sub => {
                      const subCount    = getTabCount(sub.id);
                      const isSubActive = activeTab === sub.id;
                      return (
                        <button
                          key={sub.id}
                          onClick={() => selectTab(sub.id)}
                          className={cn(
                            'flex items-center gap-2 w-full px-3.5 py-2.5 text-xs text-left transition-colors',
                            isSubActive
                              ? 'bg-primary-50 text-primary-600 font-medium'
                              : 'text-text-secondary hover:bg-gray-50 hover:text-text-primary'
                          )}
                        >
                          <sub.icon size={13} className="flex-shrink-0" />
                          <span className="flex-1">{sub.label}</span>
                          {subCount !== null && subCount > 0 && (
                            <span className={cn(
                              'px-1.5 py-0.5 rounded-full text-[10px] font-semibold',
                              isSubActive ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-text-secondary'
                            )}>{subCount}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Backdrop for open dropdown */}
        {openGroup && <div className="fixed inset-0 z-10" onClick={() => setOpenGroup(null)} />}

        {/* Tab content */}
        <div
          className={cn('p-5', activeTab !== 'antecedentes' && 'flex-1 overflow-y-auto')}
          onClick={() => openGroup && setOpenGroup(null)}
        >
          {/* Título de sección para sub-tabs de grupo */}
          {isActiveInGroup && activeLeafItem && ActiveLeafIcon && (
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100 flex-shrink-0">
              <ActiveLeafIcon size={14} className="text-primary-500" />
              <h3 className="text-sm font-semibold text-text-primary">{activeLeafItem.label}</h3>
            </div>
          )}

          {/* ── Consultas (citas futuras + historial) ── */}
          {activeTab === 'consultas' && (
            <div className="flex flex-col gap-4">

              {/* Pill tabs */}
              <div className="flex gap-1.5 bg-gray-100 p-1 rounded-xl self-start">
                <button
                  onClick={() => setConsultasSubTab('activa')}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                    consultasSubTab === 'activa'
                      ? 'bg-white text-text-primary shadow-sm'
                      : 'text-text-secondary hover:text-text-primary'
                  )}
                >
                  <Activity size={12} className={citaActiva ? 'text-green-600' : undefined} />
                  Activa
                  {citaActiva && (
                    <span className={cn(
                      'w-1.5 h-1.5 rounded-full bg-green-500',
                      consultasSubTab !== 'activa' && 'animate-pulse'
                    )} />
                  )}
                </button>
                <button
                  onClick={() => setConsultasSubTab('proximas')}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                    consultasSubTab === 'proximas'
                      ? 'bg-white text-text-primary shadow-sm'
                      : 'text-text-secondary hover:text-text-primary'
                  )}
                >
                  <Clock size={12} />
                  Próximas
                  {citasFuturas.length > 0 && (
                    <span className={cn(
                      'px-1.5 py-0.5 rounded-full text-[10px] font-semibold',
                      consultasSubTab === 'proximas' ? 'bg-primary-100 text-primary-700' : 'bg-gray-200 text-text-secondary'
                    )}>{citasFuturas.length}</span>
                  )}
                </button>
                <button
                  onClick={() => setConsultasSubTab('historial')}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                    consultasSubTab === 'historial'
                      ? 'bg-white text-text-primary shadow-sm'
                      : 'text-text-secondary hover:text-text-primary'
                  )}
                >
                  <Stethoscope size={12} />
                  Historial
                  {(citasPasadas.length + (consultas as any[]).length) > 0 && (
                    <span className={cn(
                      'px-1.5 py-0.5 rounded-full text-[10px] font-semibold',
                      consultasSubTab === 'historial' ? 'bg-primary-100 text-primary-700' : 'bg-gray-200 text-text-secondary'
                    )}>{citasPasadas.length + (consultas as any[]).length}</span>
                  )}
                </button>
              </div>

              {/* Activa */}
              {consultasSubTab === 'activa' && (
                !citaActiva ? (
                  <p className="text-sm text-text-secondary text-center py-8">Sin consulta activa</p>
                ) : (
                  <div className="space-y-2">
                    {(() => {
                      const c    = citaActiva;
                      const fi   = new Date(c.fechaInicio);
                      const dia  = fi.getDate();
                      const mes  = fi.toLocaleDateString('es-MX', { month: 'short' });
                      const dow  = fi.toLocaleDateString('es-MX', { weekday: 'long' });
                      const hora = fi.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
                      const dur  = c.fechaFin ? duracionMin(c.fechaInicio, c.fechaFin) : null;
                      const est  = getEstadoCitaInfo(c.estado);
                      return (
                        <button
                          key={c.id}
                          onClick={() => navigate(`/consultas/${c.id}`)}
                          className="w-full text-left flex items-center gap-4 px-4 py-3 rounded-lg border border-green-200 bg-green-50/50 hover:bg-green-50 transition-colors"
                        >
                          <div className="flex-shrink-0 w-12 flex flex-col items-center bg-primary-600 rounded-lg py-2 text-white">
                            <p className="text-[10px] font-semibold uppercase leading-none opacity-80">{mes}</p>
                            <p className="text-xl font-bold leading-none mt-0.5">{dia}</p>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-text-secondary capitalize">{dow}</p>
                            <div className="flex items-center gap-3 mt-0.5">
                              <p className="text-sm font-semibold text-text-primary">{hora}</p>
                              {dur && <p className="text-xs text-text-secondary">{dur} min</p>}
                            </div>
                            {c.medico && <p className="text-xs text-text-secondary mt-0.5">{c.medico.nombre}</p>}
                            {c.servicioNombre && (
                              <p className="text-xs text-primary-600 font-medium mt-0.5 truncate">{c.servicioNombre}</p>
                            )}
                            {c.motivo && <p className="text-xs text-text-secondary/70 italic mt-0.5 truncate">{c.motivo}</p>}
                          </div>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${est.softCls}`}>{est.label}</span>
                        </button>
                      );
                    })()}
                  </div>
                )
              )}

              {/* Próximas */}
              {consultasSubTab === 'proximas' && (
                citasFuturas.length === 0 ? (
                  <p className="text-sm text-text-secondary text-center py-8">Sin citas próximas</p>
                ) : (
                  <div className="space-y-2">
                    {citasFuturas.map((c: any) => {
                      const fi   = new Date(c.fechaInicio);
                      const dia  = fi.getDate();
                      const mes  = fi.toLocaleDateString('es-MX', { month: 'short' });
                      const dow  = fi.toLocaleDateString('es-MX', { weekday: 'long' });
                      const hora = fi.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
                      const dur  = c.fechaFin ? duracionMin(c.fechaInicio, c.fechaFin) : null;
                      const est  = getEstadoCitaInfo(c.estado);
                      return (
                        <div key={c.id} className="flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-100 bg-gray-50/50">
                          <div className="flex-shrink-0 w-12 flex flex-col items-center bg-primary-600 rounded-lg py-2 text-white">
                            <p className="text-[10px] font-semibold uppercase leading-none opacity-80">{mes}</p>
                            <p className="text-xl font-bold leading-none mt-0.5">{dia}</p>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-text-secondary capitalize">{dow}</p>
                            <div className="flex items-center gap-3 mt-0.5">
                              <p className="text-sm font-semibold text-text-primary">{hora}</p>
                              {dur && <p className="text-xs text-text-secondary">{dur} min</p>}
                            </div>
                            {c.medico && <p className="text-xs text-text-secondary mt-0.5">{c.medico.nombre}</p>}
                            {c.servicioNombre && (
                              <p className="text-xs text-primary-600 font-medium mt-0.5 truncate">{c.servicioNombre}</p>
                            )}
                            {c.motivo && <p className="text-xs text-text-secondary/70 italic mt-0.5 truncate">{c.motivo}</p>}
                          </div>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${est.softCls}`}>{est.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {/* Historial */}
              {consultasSubTab === 'historial' && (
                citasPasadas.length === 0 && (consultas as any[]).length === 0 ? (
                  <p className="text-sm text-text-secondary text-center py-8">Sin historial registrado</p>
                ) : (
                  <div className="space-y-2">
                    {citasPasadas.map((c: any) => {
                      const fi   = new Date(c.fechaInicio);
                      const hora = fi.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
                      const dur  = c.fechaFin ? duracionMin(c.fechaInicio, c.fechaFin) : null;
                      const est  = getEstadoCitaInfo(c.estado);
                      return (
                        <div key={`cita-${c.id}`} className="flex items-center justify-between px-4 py-3 rounded-lg border border-gray-100">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="flex-shrink-0 text-center w-10">
                              <p className="text-xs font-bold text-text-primary">{fi.getDate()}</p>
                              <p className="text-[10px] text-text-secondary capitalize">{fi.toLocaleDateString('es-MX', { month: 'short' })}</p>
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-text-primary">{hora}</p>
                                {dur && <p className="text-xs text-text-secondary">{dur} min</p>}
                              </div>
                              {c.medico && <p className="text-xs text-text-secondary mt-0.5">{c.medico.nombre}</p>}
                              {c.servicioNombre && (
                                <p className="text-xs text-primary-600 font-medium mt-0.5 truncate">{c.servicioNombre}</p>
                              )}
                              {c.motivo && <p className="text-xs text-text-secondary/70 italic mt-0.5 truncate">{c.motivo}</p>}
                            </div>
                          </div>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ml-3 ${est.softCls}`}>{est.label}</span>
                        </div>
                      );
                    })}
                    {(consultas as any[]).map((c: any) => {
                      const e = ESTADO_CONSULTA[c.estado] || { label: c.estado, cls: 'bg-gray-100 text-gray-600' };
                      return (
                        <button key={`consulta-${c.id}`} onClick={() => navigate(`/consultas/${c.id}`)}
                          className="w-full text-left flex items-center justify-between px-4 py-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                              <Stethoscope size={13} className="text-blue-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-text-primary">{c.medico?.nombre || 'Médico'}</p>
                              <p className="text-xs text-text-secondary mt-0.5">{c.createdAt ? formatDate(c.createdAt) : '—'}</p>
                            </div>
                          </div>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ml-3 ${e.cls}`}>{e.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )
              )}
            </div>
          )}

          {/* Diagnósticos */}
          {activeTab === 'diagnosticos' && (
            <DiagnosticosTab pacienteId={id!} canEdit={canVerExpediente} />
          )}

          {/* Exploraciones */}
          {activeTab === 'exploraciones' && (
            <ExploracionesTab id={id!} canEdit={canVerExpediente} />
          )}

          {/* Formularios clínicos dinámicos */}
          {activeTab === 'exploraciones-dinamicas' && (
            <ExploracionesDinamicasTab id={id!} canEdit={canVerExpediente} />
          )}

          {/* Recetas */}
          {activeTab === 'recetas' && (
            <RecetasTab id={id!} canEdit={canVerExpediente} paciente={paciente} />
          )}

          {/* Estudios */}
          {activeTab === 'estudios' && (
            <EstudiosTab id={id!} canEdit={canVerExpediente} paciente={paciente} />
          )}

          {/* Antecedentes */}
          {activeTab === 'antecedentes' && (
            <AntecedentesTab id={id!} hist={hist} canEdit={canVerExpediente} />
          )}

          {/* Signos vitales */}
          {activeTab === 'signos-vitales' && (
            <SignosVitalesTab id={id!} canEdit={canVerExpediente} />
          )}

          {/* Medicamentos activos */}
          {activeTab === 'medicamentos' && (
            <MedicamentosActivosTab id={id!} canEdit={canVerExpediente} />
          )}

          {/* Notas */}
          {activeTab === 'notas' && (
            <NotasPacienteTab id={id!} canEdit={canVerExpediente} />
          )}

          {/* Archivos */}
          {activeTab === 'archivos' && (
            <DocumentosPacienteTab id={id!} canEdit={canEditar} />
          )}

          {/* Consentimientos */}
          {activeTab === 'consentimientos' && (
            <ConsentimientosTab id={id!} canEdit={canVerExpediente} paciente={paciente} />
          )}

          {/* Finanzas */}
          {activeTab === 'finanzas' && (
            <FinanzasTab pacienteId={id!} citas={citas as any[]} />
          )}

          {/* Tareas */}
          {activeTab === 'tareas' && (
            <TareasRelacionadasPanel entidadTipo="paciente" entidadId={id!} etiqueta={nombre} />
          )}

          {/* ARCO / Privacidad */}
          {activeTab === 'arco' && (
            <ArcoTab pacienteId={id!} canEdit={canEditar} />
          )}
        </div>
      </div>

      {/* ── Modal: Editar paciente ── */}
      {editOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex flex-col justify-end md:justify-center md:items-center md:p-4">
          <div className="bg-white rounded-t-3xl md:rounded-2xl shadow-xl w-full md:max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-center pt-3 pb-1 md:hidden">
              <div className="w-10 h-1 rounded-full bg-gray-200" />
            </div>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
              <h3 className="font-semibold text-text-primary">Editar paciente</h3>
              <button onClick={() => setEditOpen(false)} className="p-1 rounded-lg hover:bg-gray-100 text-text-secondary"><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmitEdit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Nombre *</label>
                  <input required className={inputCls} value={form.nombre} onChange={ff('nombre')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Apellido paterno *</label>
                  <input required className={inputCls} value={form.apellidoPaterno} onChange={ff('apellidoPaterno')} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Apellido materno</label>
                  <input className={inputCls} value={form.apellidoMaterno} onChange={ff('apellidoMaterno')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Fecha de nacimiento</label>
                  <input type="date" className={inputCls} value={form.fechaNacimiento} onChange={ff('fechaNacimiento')} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Género</label>
                  <select className={inputCls} value={form.genero} onChange={ff('genero')}>
                    {GENEROS.map(g => <option key={g} value={g}>{GENERO_LABEL[g]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Tipo de sangre</label>
                  <select className={inputCls} value={form.tipoSangre} onChange={ff('tipoSangre')}>
                    {TIPOS_SANGRE.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Teléfono</label>
                  <input className={inputCls} value={form.telefono} onChange={ff('telefono')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Email</label>
                  <input type="email" className={inputCls} value={form.email} onChange={ff('email')} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">CURP</label>
                <input className={inputCls} value={form.curp} onChange={ff('curp')} maxLength={18} placeholder="XAXX010101HXXXXX00" />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Médico titular</label>
                <select className={inputCls} value={form.medicoTitularId} onChange={ff('medicoTitularId')}>
                  <option value="">Sin asignar</option>
                  {(medicos as any[]).map((m: any) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Alergias conocidas</label>
                <textarea rows={2} className={`${inputCls} resize-none`} value={form.alergias} onChange={ff('alergias')} placeholder="Penicilina, látex..." />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditOpen(false)} className="flex-1 border border-gray-200 text-text-secondary py-2 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={updateMutation.isPending} className="flex-1 bg-primary-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60">
                  {updateMutation.isPending ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Nueva cita ── */}
      {nuevaCitaOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex flex-col justify-end md:justify-center md:items-center md:p-4">
          <div className="bg-white rounded-t-3xl md:rounded-2xl shadow-xl w-full md:max-w-md max-h-[90vh] flex flex-col">
            <div className="flex justify-center pt-3 pb-1 md:hidden flex-shrink-0">
              <div className="w-10 h-1 rounded-full bg-gray-200" />
            </div>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="font-semibold text-text-primary">Nueva cita — {paciente.nombre}</h3>
              <button onClick={() => setNuevaCitaOpen(false)} className="p-1 rounded-lg hover:bg-gray-100 text-text-secondary"><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmitCita} className="p-6 space-y-4 overflow-y-auto flex-1">

              {/* Médico */}
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Médico *</label>
                <select required className={inputCls} value={citaForm.medicoId} onChange={cf('medicoId')}>
                  <option value="">Seleccionar médico</option>
                  {(medicos as any[]).map((m: any) => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                </select>
              </div>

              {/* Fecha + Duración */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Fecha *</label>
                  <input required type="date" className={inputCls} value={citaForm.fecha} onChange={cf('fecha')}
                    min={new Date().toISOString().split('T')[0]} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Duración (min)</label>
                  <select className={inputCls} value={citaForm.duracion} onChange={cf('duracion')}>
                    {[15, 20, 30, 45, 60, 90].map(d => <option key={d} value={d}>{d} min</option>)}
                  </select>
                </div>
              </div>

              {/* Selector de slots */}
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-2">
                  Horario disponible *
                  {citaForm.hora && (
                    <span className="ml-2 text-primary-600 font-semibold">{citaForm.hora}</span>
                  )}
                </label>
                {!citaForm.medicoId || !citaForm.fecha ? (
                  <p className="text-xs text-center py-2.5 text-text-secondary bg-gray-50 rounded-lg border border-dashed border-gray-200">
                    Selecciona médico y fecha para ver disponibilidad
                  </p>
                ) : loadingSlots ? (
                  <p className="text-xs text-center py-3 text-text-secondary">Consultando disponibilidad...</p>
                ) : !slotsData?.slots?.length ? (
                  <p className="text-xs text-center py-3 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
                    {slotsData?.mensaje ?? 'Sin horarios disponibles para esta fecha'}
                  </p>
                ) : (
                  <div className="grid grid-cols-5 gap-1.5 max-h-40 overflow-y-auto pr-0.5">
                    {slotsData.slots.map((slot: { inicio: string; fin: string }) => (
                      <button
                        key={slot.inicio} type="button"
                        onClick={() => setCitaForm(prev => ({ ...prev, hora: slot.inicio }))}
                        className={cn(
                          'py-1.5 rounded-lg text-xs font-medium border transition-colors',
                          citaForm.hora === slot.inicio
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white text-text-primary border-gray-200 hover:border-primary-400 hover:bg-primary-50'
                        )}
                      >
                        {slot.inicio}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Tipo + Motivo */}
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Tipo</label>
                <select className={inputCls} value={citaForm.tipo} onChange={cf('tipo')}>
                  <option value="presencial">Presencial</option>
                  <option value="teleconsulta">Teleconsulta</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Motivo</label>
                <input className={inputCls} value={citaForm.motivo} onChange={cf('motivo')} placeholder="Consulta de rutina..." />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setNuevaCitaOpen(false)}
                  className="flex-1 border border-gray-200 text-text-secondary py-2 rounded-lg text-sm hover:bg-gray-50">
                  Cancelar
                </button>
                <button type="submit"
                  disabled={citaMutation.isPending || !citaForm.hora}
                  className="flex-1 bg-primary-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors">
                  {citaMutation.isPending ? 'Agendando...' : 'Agendar cita'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
